'use client';
import { useMemo } from 'react';
import { signOut, useSession } from 'next-auth/react';
import { Avatar, Flex, Text, useColorMode } from '@eqi-investimentos/react';
import { useT } from '@/libs/i18n/utils/useT';
import { envConfig } from '@/config/env';
import { PATHS } from '@/config/paths';
import { Drawer } from '@/components/modules/Drawer';
import { useGetCustomerStatus } from '@/modules/Customer/http/client/useGetCustomerStatus';
import { useGetSuggestedWallet } from '@/modules/Wallet/http/client/useGetSuggestedWallet';
import { MY_PROFILE_DRAWER_ID, PERSONAL_INFO_DRAWER_ID } from './utils';
import { ProfileItem } from './Item';
import { PersonalInfoDrawer } from './PersonalInfo';
import { SUPPORT_DRAWER_ID, SupportDrawer } from './Support';

export const ProfileDrawer = () => {
  const t = useT();
  const {
    data: walletInvestorProfile = {
      profile_title: '',
      categories: [],
      strategies: [],
      subcategories: [],
    },
  } = useGetSuggestedWallet();
  const { data: accountStatus = { isEqi: false } } = useGetCustomerStatus();

  const { toggleColorMode, colorMode } = useColorMode();
  const { data: session } = useSession();

  const username = session && session.user ? session.user.name : 'Usuário';

  const config = [
    {
      id: colorMode === 'dark' ? 'themeDark' : 'theme',
      leftIcon: 'Brightness',
      onChange: toggleColorMode,
      type: 'switch',
      value: colorMode === 'light',
    },
    {
      id: 'profileData',
      leftIcon: 'PersonOutline',
      type: 'action',
      onClick: () =>
        window.dispatchEvent(
          new CustomEvent(`open-chakra-modal-${PERSONAL_INFO_DRAWER_ID}`)
        ),
    },
    {
      id: 'bookmark',
      leftIcon: 'BookmarkOutline',
      isExternal: false,
      path: PATHS.user.myList,
      type: 'navigation',
    },
    {
      id: 'history',
      leftIcon: 'HistoryOutline',
      isExternal: false,
      path: PATHS.user.myHistoric,
      type: 'navigation',
    },
    {
      id: 'termsOfUse',
      leftIcon: 'ListAltOutline',
      path: 'https://eqi.com.br/politicas',
      isExternal: true,
      type: 'navigation',
    },
    {
      id: 'privacyPolicy',
      leftIcon: 'LockOutline',
      path: 'https://eqi.com.br/politicas',
      isExternal: true,
      type: 'navigation',
    },
    {
      id: 'customerServices',
      leftIcon: 'ContactSupportOutline',
      type: 'action',
      onClick: () =>
        window.dispatchEvent(
          new CustomEvent(`open-chakra-modal-${SUPPORT_DRAWER_ID}`)
        ),
    },
  ] as const;

  const accountDetails = useMemo(
    () =>
      `Perfil ${walletInvestorProfile.profile_title}${accountStatus.isEqi ? ` • ${accountStatus.isEqiDigital ? 'Cliente Digital' : 'Cliente Assessorado'}` : ''}`,
    [accountStatus, walletInvestorProfile]
  );

  return (
    <>
      <Drawer
        id={MY_PROFILE_DRAWER_ID}
        showIconButton
        headerContent={
          <Text size="2xl" color="content.primary">
            {t('drawers.profile.header')}
          </Text>
        }
        headerProps={{
          alignItems: 'center',
          justifyContent: 'center',
          pl: '4.25rem',
          pt: '3xl',
        }}
        closeButtonProps={{ top: '5xl' }}
        bodyProps={{ px: '2xl' }}
        backButtonProps={{ buttonProps: { top: '3xl', left: '2xl' } }}
      >
        <Flex flexDir="column" gap="3xl">
          <Flex w="full" alignItems="center" gap="s">
            <Avatar name={username} showLabels={false} />
            <Flex flexDir="column">
              <Text size="xl" color="content.brand">
                {username}
              </Text>
              <Text size="m" color="content.secondary">
                {accountDetails}
              </Text>
            </Flex>
          </Flex>
          <Flex flexDir="column">
            {config.map(({ id, ...props }) => (
              <ProfileItem
                key={id}
                title={t(`drawers.profile.menu.${id}`)}
                {...props}
              />
            ))}
            <ProfileItem
              ID="button_logout_profile_drawer"
              title={t('drawers.profile.logoutButton')}
              showLeftIcon
              leftIcon="Logout"
              type="action"
              onClick={signOut}
              showArrow={false}
              pt="2.375rem"
            />
          </Flex>
          <Flex
            flexDir="column"
            w="full"
            h="2rem"
            my="2rem"
            alignItems="center"
            justifyContent="center"
          >
            <Text size="l" color="content.secondary">
              {`${t('drawers.profile.appVersion')} ${envConfig?.appVersion}`}
            </Text>
          </Flex>
        </Flex>
      </Drawer>
      <PersonalInfoDrawer />
      <SupportDrawer />
    </>
  );
};
